import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  User, Calendar, DollarSign, FileText, LogOut, Briefcase, Clock, Download,
  CheckCircle, XCircle, AlertCircle, Phone, MapPin, CreditCard, Heart, Shield,
  Award, Loader2, CalendarPlus, Sun, Stethoscope, Hotel, Settings, Users,
  Plus, Edit, Trash2, Eye, History, Upload, Globe, Building2, ClipboardList,
  Timer, Flag, Play, FileSpreadsheet, CalendarDays, UserCheck, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Singapore-specific constants
const CITIZENSHIP_TYPES = [
  { value: 'singapore_citizen', label: 'Singapore Citizen' },
  { value: 'permanent_resident', label: 'Permanent Resident' },
  { value: 'foreigner', label: 'Foreigner' }
];

const PR_YEARS = [
  { value: '1st_year', label: '1st Year PR' },
  { value: '2nd_year', label: '2nd Year PR' },
  { value: '3rd_year_onwards', label: '3rd Year+ PR' }
];

const EMPLOYEE_ROLES = [
  { value: 'employee', label: 'Employee' },
  { value: 'manager', label: 'Manager' },
  { value: 'hr_admin', label: 'HR Admin' },
  { value: 'payroll_preparer', label: 'Payroll Preparer' },
  { value: 'payroll_approver', label: 'Payroll Approver' }
];

const PAY_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly (30th)' },
  { value: 'fortnightly', label: 'Fortnightly (15th & 30th)' }
];

// ==================== GOOGLE AUTH HANDLER ====================
const AuthCallback = ({ onAuthSuccess }) => {
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error('Invalid authentication response');
        window.location.href = '/hr-portal';
        return;
      }
      
      const sessionId = sessionIdMatch[1];
      
      try {
        const response = await fetch(`${API_URL}/api/hr/auth/google/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Authentication failed');
        }
        
        const data = await response.json();
        localStorage.setItem('hr_token', data.access_token);
        localStorage.setItem('hr_employee', JSON.stringify(data.employee));
        localStorage.setItem('hr_is_admin', data.is_admin ? 'true' : 'false');
        
        // Clear hash and redirect
        window.history.replaceState(null, '', '/hr-portal');
        onAuthSuccess(data.employee, data.access_token, data.is_admin);
        toast.success('Login successful!');
      } catch (error) {
        toast.error(error.message);
        window.location.href = '/hr-portal';
      }
    };
    
    processAuth();
  }, [onAuthSuccess]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal mx-auto mb-4" />
          <p className="text-muted-foreground">Authenticating...</p>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== LOGIN COMPONENT ====================
const LoginForm = () => {
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/hr-portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <Card className="w-full max-w-md border-border/50">
        <CardHeader className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_1f762a5e-4e34-4452-aabe-298bcfb2d13e/artifacts/9emip28a_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg"
            alt="SusTek"
            className="h-10 mx-auto mb-4"
          />
          <CardTitle className="text-2xl">HR Portal</CardTitle>
          <CardDescription>Sign in with your company Google account</CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="outline" 
            className="w-full h-12 text-base"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Only registered employees can access the HR Portal.
            <br />Contact HR if you need access.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== COUNTRY SELECT ====================
const CountrySelect = ({ value, onChange, disabled }) => {
  const [countries, setCountries] = useState([]);
  
  useEffect(() => {
    fetch(`${API_URL}/api/hr/countries`)
      .then(res => res.json())
      .then(data => setCountries(data.countries || []))
      .catch(() => setCountries(['Singapore']));
  }, []);
  
  return (
    <select
      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
      value={value || 'Singapore'}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {countries.map(country => (
        <option key={country} value={country}>{country}</option>
      ))}
    </select>
  );
};

// ==================== PROFILE TAB ====================
const ProfileTab = ({ employee, token, onProfileUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone: employee.phone || '',
    address: employee.address || '',
    country_of_residence: employee.country_of_residence || 'Singapore',
    emergency_contact_name: employee.emergency_contact_name || '',
    emergency_contact_phone: employee.emergency_contact_phone || '',
    bank_name: employee.bank_name || '',
    bank_account_number: employee.bank_account_number || '',
  });

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/hr/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to update profile');
      const updated = await response.json();
      onProfileUpdate(updated);
      setIsEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">My Profile</h2>
          <p className="text-muted-foreground">View and update your personal information</p>
        </div>
        {!isEditing ? (
          <Button variant="outline" onClick={() => setIsEditing(true)}>Edit Profile</Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isLoading}>Cancel</Button>
            <Button variant="teal" onClick={handleSave} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><User className="h-5 w-5 text-teal" />Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div><Label className="text-muted-foreground">Full Name</Label><p className="font-medium">{employee.first_name} {employee.last_name}</p></div>
            <div><Label className="text-muted-foreground">Email</Label><p className="font-medium">{employee.email}</p></div>
            <div><Label className="text-muted-foreground">Position</Label><p className="font-medium">{employee.position}</p></div>
            <div><Label className="text-muted-foreground">Department</Label><p className="font-medium">{employee.department}</p></div>
            <div><Label className="text-muted-foreground">Start Date</Label><p className="font-medium">{new Date(employee.start_date).toLocaleDateString('en-SG', { dateStyle: 'long' })}</p></div>
            <div>
              <Label>Country of Residence</Label>
              {isEditing ? (
                <CountrySelect value={formData.country_of_residence} onChange={(v) => setFormData({...formData, country_of_residence: v})} />
              ) : (
                <p className="font-medium">{employee.country_of_residence || 'Singapore'}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Phone className="h-5 w-5 text-teal" />Contact Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Phone</Label>
              {isEditing ? <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+65 9123 4567" /> : <p className="font-medium">{employee.phone || '-'}</p>}
            </div>
            <div>
              <Label>Address</Label>
              {isEditing ? <Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} rows={3} /> : <p className="font-medium">{employee.address || '-'}</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Heart className="h-5 w-5 text-red-500" />Emergency Contact</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Contact Name</Label>
              {isEditing ? <Input value={formData.emergency_contact_name} onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})} /> : <p className="font-medium">{employee.emergency_contact_name || '-'}</p>}
            </div>
            <div>
              <Label>Contact Phone</Label>
              {isEditing ? <Input value={formData.emergency_contact_phone} onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})} /> : <p className="font-medium">{employee.emergency_contact_phone || '-'}</p>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-teal" />Bank Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Bank Name</Label>
              {isEditing ? <Input value={formData.bank_name} onChange={(e) => setFormData({...formData, bank_name: e.target.value})} placeholder="DBS, OCBC, UOB" /> : <p className="font-medium">{employee.bank_name || '-'}</p>}
            </div>
            <div>
              <Label>Account Number</Label>
              {isEditing ? <Input value={formData.bank_account_number} onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})} /> : <p className="font-medium">{employee.bank_account_number ? '••••' + employee.bank_account_number.slice(-4) : '-'}</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== ENTITLEMENTS TAB ====================
const EntitlementsTab = ({ token }) => {
  const [entitlements, setEntitlements] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/hr/entitlements`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(res => res.json())
      .then(setEntitlements)
      .catch(() => toast.error('Failed to load entitlements'))
      .finally(() => setIsLoading(false));
  }, [token]);

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Entitlements & Benefits</h2></div>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-teal" />Leave Entitlements</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2 mb-2"><Sun className="h-5 w-5 text-blue-500" /><span className="font-semibold">Vacation</span></div>
              <p className="text-2xl font-bold text-blue-600">15 days</p>
            </div>
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="flex items-center gap-2 mb-2"><Stethoscope className="h-5 w-5 text-red-500" /><span className="font-semibold">Medical</span></div>
              <p className="text-2xl font-bold text-red-600">14 days</p>
            </div>
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2"><Hotel className="h-5 w-5 text-purple-500" /><span className="font-semibold">Hospitalization</span></div>
              <p className="text-2xl font-bold text-purple-600">60 days</p>
            </div>
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-2 mb-2"><Calendar className="h-5 w-5 text-green-500" /><span className="font-semibold">Public Holidays</span></div>
              <p className="text-lg font-bold text-green-600">Statutory</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== TIMESHEET TAB (Mobile-Friendly) ====================
const TimesheetTab = ({ token }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [deadlineInfo, setDeadlineInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [entryForm, setEntryForm] = useState({ date: new Date().toISOString().split('T')[0], hours: 8, project_code: '', activity_notes: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = () => {
    const today = new Date().toISOString().split('T')[0];
    Promise.all([
      fetch(`${API_URL}/api/hr/timesheets`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }).then(r => r.json()),
      fetch(`${API_URL}/api/hr/timesheets/deadline?date=${today}`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }).then(r => r.json()),
    ]).then(([t, d]) => { setTimesheets(t.timesheets || []); setDeadlineInfo(d); }).finally(() => setIsLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  // Quick hour presets for mobile
  const hourPresets = [4, 6, 8, 9, 10, 12];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/timesheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(entryForm),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      const result = await res.json();
      toast.success(result.is_late ? 'Timesheet submitted (LATE)' : 'Timesheet submitted!');
      setShowForm(false);
      setEntryForm({ date: new Date().toISOString().split('T')[0], hours: 8, project_code: '', activity_notes: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
    finally { setIsSubmitting(false); }
  };

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - stacked on mobile */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div><h2 className="text-xl sm:text-2xl font-bold">Daily Timesheet</h2></div>
        <Button variant="teal" onClick={() => setShowForm(true)} className="w-full sm:w-auto" data-testid="log-time-btn">
          <Clock className="h-4 w-4 mr-2" />Log Time
        </Button>
      </div>

      {/* Deadline Banner - compact on mobile */}
      {deadlineInfo && (
        <Card className={`${deadlineInfo.is_late ? 'border-red-500 bg-red-50' : 'border-orange-500 bg-orange-50'}`}>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Timer className={`h-5 w-5 sm:h-6 sm:w-6 ${deadlineInfo.is_late ? 'text-red-500' : 'text-orange-500'}`} />
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-sm sm:text-base ${deadlineInfo.is_late ? 'text-red-700' : 'text-orange-700'}`}>
                  {deadlineInfo.is_late ? 'Deadline Passed' : deadlineInfo.time_remaining_formatted}
                </p>
                <p className={`text-xs sm:text-sm ${deadlineInfo.is_late ? 'text-red-600' : 'text-orange-600'}`}>
                  Submit by 23:59 SGT
                </p>
              </div>
              {deadlineInfo.is_holiday && (
                <Badge className="bg-purple-500 text-xs">
                  <Flag className="h-3 w-3 mr-1" /> Holiday
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile-optimized form */}
      {showForm && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Log Timesheet Entry</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Date - full width */}
              <div>
                <Label className="text-sm">Date</Label>
                <Input type="date" value={entryForm.date} onChange={(e) => setEntryForm({...entryForm, date: e.target.value})} required className="h-12 text-base" />
              </div>
              
              {/* Quick Hour Presets - tap friendly */}
              <div>
                <Label className="text-sm">Hours Worked</Label>
                <div className="grid grid-cols-6 gap-2 mt-2 mb-2">
                  {hourPresets.map(h => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setEntryForm({...entryForm, hours: h})}
                      className={`h-10 rounded-lg font-medium text-sm transition-colors ${
                        entryForm.hours === h 
                          ? 'bg-teal text-white' 
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {h}h
                    </button>
                  ))}
                </div>
                <Input 
                  type="number" 
                  min="0.5" 
                  max="24" 
                  step="0.5" 
                  value={entryForm.hours} 
                  onChange={(e) => setEntryForm({...entryForm, hours: parseFloat(e.target.value)})} 
                  required 
                  className="h-12 text-base text-center font-medium"
                  placeholder="Or enter custom hours"
                />
              </div>
              
              {/* Project Code - optional */}
              <div>
                <Label className="text-sm">Project Code <span className="text-muted-foreground">(optional)</span></Label>
                <Input 
                  value={entryForm.project_code} 
                  onChange={(e) => setEntryForm({...entryForm, project_code: e.target.value})} 
                  placeholder="e.g., PROJ-001" 
                  className="h-12 text-base"
                />
              </div>
              
              {/* Activity Notes - larger textarea */}
              <div>
                <Label className="text-sm">Activity Notes <span className="text-muted-foreground">(optional)</span></Label>
                <Textarea 
                  value={entryForm.activity_notes} 
                  onChange={(e) => setEntryForm({...entryForm, activity_notes: e.target.value})} 
                  placeholder="Brief description of work done"
                  rows={3}
                  className="text-base resize-none"
                />
              </div>
              
              {/* Actions - stacked on mobile */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="h-12 sm:h-10 order-2 sm:order-1">
                  Cancel
                </Button>
                <Button type="submit" variant="teal" disabled={isSubmitting} className="h-12 sm:h-10 flex-1 order-1 sm:order-2" data-testid="submit-timesheet-btn">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Timesheet'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><ClipboardList className="h-5 w-5 text-teal" />Recent Entries</CardTitle></CardHeader>
        <CardContent>
          {timesheets.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No timesheet entries yet. Tap "Log Time" to add your first entry.</p>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="sm:hidden space-y-3">
                {timesheets.slice(0, 14).map(ts => (
                  <div key={ts.id} className="p-3 rounded-lg border bg-card">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{new Date(ts.date).toLocaleDateString('en-SG', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      <Badge className={
                        ts.status === 'approved' ? 'bg-green-500' : 
                        ts.status === 'rejected' ? 'bg-red-500' : 
                        ts.is_late ? 'bg-orange-500' : 'bg-blue-500'
                      }>
                        {ts.is_late && ts.status === 'submitted' ? 'Late' : ts.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-2xl font-bold text-teal">{ts.hours}h</span>
                      {ts.project_code && <Badge variant="outline">{ts.project_code}</Badge>}
                    </div>
                    {ts.activity_notes && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{ts.activity_notes}</p>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Desktop table view */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Hours</th>
                      <th className="text-left p-2">Project</th>
                      <th className="text-left p-2">Notes</th>
                      <th className="text-left p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timesheets.slice(0, 14).map(ts => (
                      <tr key={ts.id} className="border-b">
                        <td className="p-2">{new Date(ts.date).toLocaleDateString('en-SG')}</td>
                        <td className="p-2 font-medium">{ts.hours}h</td>
                        <td className="p-2">{ts.project_code || '-'}</td>
                        <td className="p-2 truncate max-w-xs">{ts.activity_notes || '-'}</td>
                        <td className="p-2">
                          <Badge className={
                            ts.status === 'approved' ? 'bg-green-500' : 
                            ts.status === 'rejected' ? 'bg-red-500' : 
                            ts.is_late ? 'bg-orange-500' : 'bg-blue-500'
                          }>
                            {ts.is_late && ts.status === 'submitted' ? 'Late' : ts.status}
                          </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ONBOARDING TAB ====================
const OnboardingTab = ({ token, employee }) => {
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 0, percentage: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = () => {
    fetch(`${API_URL}/api/hr/onboarding/tasks`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(r => r.json())
      .then(data => { setTasks(data.tasks || []); setProgress(data.progress); })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const completeTask = async (taskId) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/onboarding/tasks/${taskId}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to complete task');
      const result = await res.json();
      toast.success(result.all_complete ? 'All onboarding tasks complete! 🎉' : 'Task completed!');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  if (employee?.onboarding_complete || tasks.length === 0) {
    return (
      <div className="space-y-6">
        <div><h2 className="text-2xl font-bold">Onboarding</h2></div>
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <p className="text-xl font-semibold text-green-700">Onboarding Complete!</p>
            <p className="text-muted-foreground">You've completed all your onboarding tasks.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Onboarding</h2></div>
      
      {/* Progress Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">Progress</span>
            <span className="text-sm text-muted-foreground">{progress.completed}/{progress.total} tasks ({progress.percentage}%)</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-teal h-3 rounded-full transition-all" style={{ width: `${progress.percentage}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Tasks by Category */}
      {['documents', 'compliance', 'it_setup', 'training'].map(category => {
        const categoryTasks = tasks.filter(t => t.category === category);
        if (categoryTasks.length === 0) return null;
        
        const categoryLabels = { documents: 'Documents', compliance: 'Compliance', it_setup: 'IT Setup', training: 'Training' };
        const categoryIcons = { documents: FileText, compliance: Shield, it_setup: Settings, training: Award };
        const Icon = categoryIcons[category];
        
        return (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-teal" />{categoryLabels[category]}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {categoryTasks.map(task => (
                <div key={task.id} className={`flex items-center justify-between p-3 rounded-lg border ${task.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-background'}`}>
                  <div>
                    <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{task.task_name}</p>
                    {task.description && <p className="text-sm text-muted-foreground">{task.description}</p>}
                  </div>
                  {task.status === 'completed' ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => completeTask(task.id)}>
                      <Play className="h-3 w-3 mr-1" /> Complete
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ==================== TEAM CALENDAR TAB ====================
const TeamCalendarTab = ({ token }) => {
  const [availability, setAvailability] = useState(null);
  const [leaveCalendar, setLeaveCalendar] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [avail, cal] = await Promise.all([
        fetch(`${API_URL}/api/hr/team/availability`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }).then(r => r.json()),
        fetch(`${API_URL}/api/hr/team/leave-calendar?month=${selectedMonth}&year=${selectedYear}`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }).then(r => r.json()),
      ]);
      setAvailability(avail);
      setLeaveCalendar(cal);
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchData(); }, [token, selectedMonth, selectedYear]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h2 className="text-2xl font-bold">Team Availability</h2>
        <div className="flex items-center gap-2">
          <select className="h-10 rounded-md border border-input bg-background px-3" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
            {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="h-10 rounded-md border border-input bg-background px-3" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* This Week Summary */}
      {availability?.summary && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100"><Users className="h-5 w-5 text-green-600" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Available This Week</p>
                  <p className="text-2xl font-bold text-green-600">{availability.summary.available}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100"><Calendar className="h-5 w-5 text-orange-600" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">On Leave</p>
                  <p className="text-2xl font-bold text-orange-600">{availability.summary.on_leave}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-teal/20"><Award className="h-5 w-5 text-teal" /></div>
                <div>
                  <p className="text-sm text-muted-foreground">Coverage Rate</p>
                  <p className="text-2xl font-bold text-teal">{availability.summary.availability_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Who's On Leave This Week */}
      {availability?.employees?.filter(e => !e.is_available).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-orange-600"><Calendar className="h-5 w-5" /> On Leave This Week</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {availability.employees.filter(e => !e.is_available).map(emp => (
                <div key={emp.employee_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-3">
                    {emp.picture ? (
                      <img src={emp.picture} alt={emp.name} className="h-10 w-10 rounded-full" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-orange-200 flex items-center justify-center">
                        <User className="h-5 w-5 text-orange-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{emp.name}</p>
                      <p className="text-sm text-muted-foreground">{emp.department} • {emp.position}</p>
                    </div>
                  </div>
                  {emp.leave && (
                    <Badge className={emp.leave.leave_type === 'vacation' ? 'bg-blue-500' : 'bg-purple-500'}>
                      {emp.leave.leave_type} ({emp.leave.days}d)
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Leave Calendar */}
      {leaveCalendar && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-teal" /> {monthNames[selectedMonth - 1]} {selectedYear} Leave Calendar</CardTitle>
            <CardDescription>{leaveCalendar.total_leaves} approved leaves • {leaveCalendar.holidays?.length || 0} public holidays</CardDescription>
          </CardHeader>
          <CardContent>
            {leaveCalendar.holidays?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-muted-foreground mb-2">Public Holidays</p>
                <div className="flex flex-wrap gap-2">
                  {leaveCalendar.holidays.map(h => (
                    <Badge key={h.date} variant="outline" className="border-red-500 text-red-700">
                      <Flag className="h-3 w-3 mr-1" /> {h.name} ({new Date(h.date).toLocaleDateString('en-SG', { month: 'short', day: 'numeric' })})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {leaveCalendar.leaves?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Employee</th>
                      <th className="text-left p-2">Department</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Dates</th>
                      <th className="text-left p-2">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveCalendar.leaves.map(l => (
                      <tr key={l.leave_id} className="border-b">
                        <td className="p-2 font-medium">{l.employee_name}</td>
                        <td className="p-2">{l.department}</td>
                        <td className="p-2"><Badge className={l.leave_type === 'vacation' ? 'bg-blue-500' : 'bg-purple-500'}>{l.leave_type}</Badge></td>
                        <td className="p-2">{new Date(l.start_date).toLocaleDateString('en-SG')} - {new Date(l.end_date).toLocaleDateString('en-SG')}</td>
                        <td className="p-2">{l.days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">No leaves scheduled for this month</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== MANAGER APPROVALS TAB ====================
const ManagerApprovalsTab = ({ token }) => {
  const [queue, setQueue] = useState({ pending_leaves: [], pending_timesheets: [], summary: { total_pending: 0 } });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [isApproving, setIsApproving] = useState(false);

  const fetchQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/manager/approval-queue`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setQueue(data);
      }
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchQueue(); }, [token]);

  const handleBulkApprove = async () => {
    if (selectedLeaves.length === 0 && selectedTimesheets.length === 0) {
      toast.error('Select items to approve');
      return;
    }
    
    setIsApproving(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/manager/bulk-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ leave_ids: selectedLeaves, timesheet_ids: selectedTimesheets })
      });
      if (!res.ok) throw new Error('Bulk approve failed');
      const result = await res.json();
      toast.success(result.message);
      setSelectedLeaves([]);
      setSelectedTimesheets([]);
      fetchQueue();
    } catch (err) { toast.error(err.message); }
    finally { setIsApproving(false); }
  };

  const toggleLeaveSelection = (id) => {
    setSelectedLeaves(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleTimesheetSelection = (id) => {
    setSelectedTimesheets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllLeaves = () => {
    if (selectedLeaves.length === queue.pending_leaves.length) {
      setSelectedLeaves([]);
    } else {
      setSelectedLeaves(queue.pending_leaves.map(l => l.id));
    }
  };

  const selectAllTimesheets = () => {
    if (selectedTimesheets.length === queue.pending_timesheets.length) {
      setSelectedTimesheets([]);
    } else {
      setSelectedTimesheets(queue.pending_timesheets.map(t => t.id));
    }
  };

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold">Approval Queue</h2>
          <p className="text-muted-foreground">{queue.summary.total_pending} items awaiting approval</p>
        </div>
        {(selectedLeaves.length > 0 || selectedTimesheets.length > 0) && (
          <Button variant="teal" onClick={handleBulkApprove} disabled={isApproving} data-testid="bulk-approve-btn">
            {isApproving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Approve Selected ({selectedLeaves.length + selectedTimesheets.length})
          </Button>
        )}
      </div>

      {/* Pending Leave Requests */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-blue-500" /> Leave Requests ({queue.pending_leaves.length})</CardTitle>
            {queue.pending_leaves.length > 0 && (
              <Button variant="ghost" size="sm" onClick={selectAllLeaves}>
                {selectedLeaves.length === queue.pending_leaves.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {queue.pending_leaves.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No pending leave requests</p>
          ) : (
            <div className="space-y-3">
              {queue.pending_leaves.map(leave => (
                <div key={leave.id} className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${selectedLeaves.includes(leave.id) ? 'bg-blue-50 border-blue-300' : 'hover:bg-muted/50'}`}>
                  <input type="checkbox" checked={selectedLeaves.includes(leave.id)} onChange={() => toggleLeaveSelection(leave.id)} className="h-4 w-4 rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{leave.employee_name}</p>
                      <Badge variant="outline">{leave.employee_department}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {leave.leave_type} • {new Date(leave.start_date).toLocaleDateString('en-SG')} to {new Date(leave.end_date).toLocaleDateString('en-SG')} • {leave.days} days
                    </p>
                    {leave.reason && <p className="text-sm text-muted-foreground mt-1">"{leave.reason}"</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Timesheets */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-500" /> Timesheets ({queue.pending_timesheets.length})
                {queue.summary?.overtime_timesheets > 0 && (
                  <Badge className="bg-amber-500 ml-2">
                    <AlertCircle className="h-3 w-3 mr-1" /> {queue.summary.overtime_timesheets} Overtime
                  </Badge>
                )}
              </CardTitle>
              {queue.summary?.requires_review > 0 && (
                <p className="text-sm text-amber-600 mt-1">{queue.summary.requires_review} entries require review (overtime/late)</p>
              )}
            </div>
            {queue.pending_timesheets.length > 0 && (
              <Button variant="ghost" size="sm" onClick={selectAllTimesheets}>
                {selectedTimesheets.length === queue.pending_timesheets.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {queue.pending_timesheets.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No pending timesheets</p>
          ) : (
            <div className="space-y-3">
              {queue.pending_timesheets.map(ts => (
                <div key={ts.id} className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                  ts.is_overtime ? 'bg-amber-50 border-amber-300' :
                  selectedTimesheets.includes(ts.id) ? 'bg-orange-50 border-orange-300' : 
                  'hover:bg-muted/50'
                }`}>
                  <input type="checkbox" checked={selectedTimesheets.includes(ts.id)} onChange={() => toggleTimesheetSelection(ts.id)} className="h-4 w-4 rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{ts.employee_name}</p>
                      {ts.is_overtime && (
                        <Badge className="bg-amber-500">
                          <AlertCircle className="h-3 w-3 mr-1" /> Overtime +{ts.overtime_hours?.toFixed(1) || (ts.hours - 8).toFixed(1)}h
                        </Badge>
                      )}
                      {ts.is_late && <Badge className="bg-red-500">Late</Badge>}
                      {ts.requires_review && !ts.is_overtime && !ts.is_late && <Badge className="bg-purple-500">Review</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(ts.date).toLocaleDateString('en-SG')} • <span className={ts.is_overtime ? 'font-semibold text-amber-700' : ''}>{ts.hours}h</span> {ts.project_code ? `• ${ts.project_code}` : ''}
                    </p>
                    {ts.activity_notes && <p className="text-sm text-muted-foreground mt-1 truncate">"{ts.activity_notes}"</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== LEAVE TAB ====================
const LeaveTab = ({ token }) => {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = () => {
    Promise.all([
      fetch(`${API_URL}/api/hr/leave/balance`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }).then(r => r.json()),
      fetch(`${API_URL}/api/hr/leave/history`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }).then(r => r.json()),
    ]).then(([b, h]) => { setBalance(b); setHistory(h); }).finally(() => setIsLoading(false));
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleApply = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/leave/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(leaveForm),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success('Leave submitted!');
      setShowForm(false);
      setLeaveForm({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (err) { toast.error(err.message); }
    finally { setIsSubmitting(false); }
  };

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div><h2 className="text-2xl font-bold">Leave Management</h2></div>
        <Button variant="teal" onClick={() => setShowForm(true)}><CalendarPlus className="h-4 w-4 mr-2" />Apply Leave</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {['vacation', 'medical', 'hospitalization'].map(type => {
          const data = balance?.[type];
          const colorMap = { vacation: 'blue', medical: 'red', hospitalization: 'purple' };
          const color = colorMap[type];
          const IconMap = { vacation: Sun, medical: Stethoscope, hospitalization: Hotel };
          const Icon = IconMap[type];
          return (
            <Card key={type} className={`border-${color}-500/30 bg-${color}-500/5`}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2"><Icon className={`h-5 w-5 text-${color}-500`} /><span className="font-medium capitalize">{type}</span></div>
                  <Badge variant="secondary">{data?.remaining || 0} left</Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">{data?.used || 0}</span>
                  <span className="text-muted-foreground">/ {data?.total || 0} used</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showForm && (
        <Card className="border-teal/30">
          <CardHeader><CardTitle>Apply for Leave</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleApply} className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Leave Type</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3" value={leaveForm.leave_type} onChange={(e) => setLeaveForm({...leaveForm, leave_type: e.target.value})}>
                    <option value="vacation">Vacation</option>
                    <option value="medical">Medical</option>
                    <option value="hospitalization">Hospitalization</option>
                  </select>
                </div>
                <div><Label>Start Date</Label><Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})} required /></div>
                <div><Label>End Date</Label><Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})} required /></div>
              </div>
              <div><Label>Reason</Label><Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} rows={2} /></div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" variant="teal" disabled={isSubmitting}>{isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Submit</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Leave History</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? <p className="text-muted-foreground text-center py-8">No records</p> : (
            <div className="space-y-3">
              {history.map(leave => (
                <div key={leave.id} className="flex justify-between items-center p-4 rounded-lg border">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${leave.leave_type === 'vacation' ? 'bg-blue-500/10 text-blue-500' : leave.leave_type === 'medical' ? 'bg-red-500/10 text-red-500' : 'bg-purple-500/10 text-purple-500'}`}>
                      {leave.leave_type === 'vacation' ? <Sun className="h-5 w-5" /> : leave.leave_type === 'medical' ? <Stethoscope className="h-5 w-5" /> : <Hotel className="h-5 w-5" />}
                    </div>
                    <div>
                      <p className="font-medium capitalize">{leave.leave_type} Leave</p>
                      <p className="text-sm text-muted-foreground">{new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()} ({leave.days} days)</p>
                    </div>
                  </div>
                  <Badge className={leave.status === 'approved' ? 'bg-green-500' : leave.status === 'rejected' ? 'bg-red-500' : ''}>{leave.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== PAYROLL TAB ====================
const PayrollTab = ({ token, employee }) => {
  const [payslips, setPayslips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/hr/payroll/payslips`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(r => r.json())
      .then(setPayslips)
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleDownload = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/payroll/payslip/${id}/download`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslip.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const isSingapore = (employee?.country_of_residence || 'Singapore') === 'Singapore';

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Payroll</h2><p className="text-muted-foreground">All amounts in SGD</p></div>
      {!isSingapore && <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30">Note: CPF deductions not applicable for non-Singapore residents</Badge>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {payslips.map(p => (
          <Card key={p.id} className="hover:border-teal/50">
            <CardHeader className="pb-2">
              <div className="flex justify-between"><CardTitle className="text-lg">{monthNames[p.month]} {p.year}</CardTitle><Badge variant="outline" className="text-teal">Paid</Badge></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Base</span><span>${p.base_salary?.toLocaleString('en-US', {minimumFractionDigits: 2})}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Deductions</span><span className="text-red-600">-${p.total_deductions?.toLocaleString('en-US', {minimumFractionDigits: 2})}</span></div>
                <Separator />
                <div className="flex justify-between font-semibold"><span>Net</span><span className="text-teal">${p.net_salary?.toLocaleString('en-US', {minimumFractionDigits: 2})}</span></div>
              </div>
              <Button variant="outline" className="w-full" onClick={() => handleDownload(p.id)}><Download className="h-4 w-4 mr-2" />Download</Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ==================== DOCUMENTS TAB ====================
const DocumentsTab = ({ token, employee }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/certificate/employment`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employment_certificate.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
    finally { setIsDownloading(false); }
  };

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Documents</h2></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center mb-2"><FileText className="h-6 w-6 text-teal" /></div>
            <CardTitle>Employment Certificate</CardTitle>
            <CardDescription>Official certificate with signature</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="teal" className="w-full" onClick={handleDownload} disabled={isDownloading}>
              {isDownloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Download
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== ADMIN COMPONENTS ====================
const AdminEmployeesTab = ({ token }) => {
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(null); // employee to assign manager
  const [newEmployee, setNewEmployee] = useState({ 
    email: '', first_name: '', last_name: '', position: '', department: '', 
    start_date: '', country_of_residence: 'Singapore', base_salary: 0, is_admin: false,
    // Singapore-specific
    citizenship_status: 'singapore_citizen', pr_year: '', work_pass_type: '', 
    date_of_birth: '', cpf_account_number: '', role: 'employee', pay_frequency: 'monthly',
    manager_id: ''
  });

  const fetchData = async () => {
    try {
      const [empRes, mgrRes] = await Promise.all([
        fetch(`${API_URL}/api/hr/admin/employees`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' }),
        fetch(`${API_URL}/api/hr/admin/managers`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      ]);
      const empData = await empRes.json();
      const mgrData = await mgrRes.json();
      setEmployees(empData.employees || []);
      setManagers(mgrData.managers || []);
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/hr/admin/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(newEmployee),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      toast.success('Employee added!');
      setShowAdd(false);
      setNewEmployee({ 
        email: '', first_name: '', last_name: '', position: '', department: '', 
        start_date: '', country_of_residence: 'Singapore', base_salary: 0, is_admin: false,
        citizenship_status: 'singapore_citizen', pr_year: '', work_pass_type: '', 
        date_of_birth: '', cpf_account_number: '', role: 'employee', pay_frequency: 'monthly',
        manager_id: ''
      });
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const toggleStatus = async (emp) => {
    try {
      await fetch(`${API_URL}/api/hr/admin/employees/${emp.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ is_active: !emp.is_active }),
      });
      fetchData();
      toast.success(`Employee ${emp.is_active ? 'deactivated' : 'activated'}`);
    } catch (err) { toast.error(err.message); }
  };

  const assignOnboarding = async (empId) => {
    try {
      await fetch(`${API_URL}/api/hr/admin/onboarding/assign/${empId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      toast.success('Onboarding tasks assigned');
    } catch (err) { toast.error(err.message); }
  };

  const assignManager = async (empId, managerId) => {
    try {
      await fetch(`${API_URL}/api/hr/admin/employees/${empId}/manager?manager_id=${managerId || ''}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      toast.success('Manager assigned');
      setShowManagerModal(null);
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const getManagerName = (managerId) => {
    if (!managerId) return '-';
    const manager = employees.find(e => e.id === managerId) || managers.find(m => m.id === managerId);
    return manager ? `${manager.first_name} ${manager.last_name}` : '-';
  };

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <h2 className="text-2xl font-bold">Manage Employees</h2>
        <Button variant="teal" onClick={() => setShowAdd(true)} data-testid="add-employee-btn"><Plus className="h-4 w-4 mr-2" />Add Employee</Button>
      </div>

      {showAdd && (
        <Card>
          <CardHeader><CardTitle>Add New Employee</CardTitle><CardDescription>Singapore-compliant employee setup with CPF configuration</CardDescription></CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2"><User className="h-4 w-4" /> Basic Information</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label>Email *</Label><Input type="email" value={newEmployee.email} onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})} required /></div>
                  <div><Label>First Name *</Label><Input value={newEmployee.first_name} onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})} required /></div>
                  <div><Label>Last Name *</Label><Input value={newEmployee.last_name} onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})} required /></div>
                  <div><Label>Date of Birth</Label><Input type="date" value={newEmployee.date_of_birth} onChange={(e) => setNewEmployee({...newEmployee, date_of_birth: e.target.value})} /></div>
                  <div><Label>Position</Label><Input value={newEmployee.position} onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})} /></div>
                  <div><Label>Department</Label><Input value={newEmployee.department} onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})} /></div>
                </div>
              </div>
              
              {/* Employment Info */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Employment Details</h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label>Start Date *</Label><Input type="date" value={newEmployee.start_date} onChange={(e) => setNewEmployee({...newEmployee, start_date: e.target.value})} required /></div>
                  <div><Label>Country</Label><CountrySelect value={newEmployee.country_of_residence} onChange={(v) => setNewEmployee({...newEmployee, country_of_residence: v})} /></div>
                  <div><Label>Annual Salary (SGD)</Label><Input type="number" value={newEmployee.base_salary} onChange={(e) => setNewEmployee({...newEmployee, base_salary: parseFloat(e.target.value) || 0})} /></div>
                  <div>
                    <Label>Role</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={newEmployee.role} onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})}>
                      {EMPLOYEE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Pay Frequency</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={newEmployee.pay_frequency} onChange={(e) => setNewEmployee({...newEmployee, pay_frequency: e.target.value})}>
                      {PAY_FREQUENCIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 pt-6"><Switch checked={newEmployee.is_admin} onCheckedChange={(v) => setNewEmployee({...newEmployee, is_admin: v})} /><Label>Admin Access</Label></div>
                </div>
              </div>

              {/* Singapore CPF Info */}
              {newEmployee.country_of_residence === 'Singapore' && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><Flag className="h-4 w-4" /> Singapore CPF Configuration</h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label>Citizenship Status *</Label>
                      <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={newEmployee.citizenship_status} onChange={(e) => setNewEmployee({...newEmployee, citizenship_status: e.target.value, pr_year: ''})}>
                        {CITIZENSHIP_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    {newEmployee.citizenship_status === 'permanent_resident' && (
                      <div>
                        <Label>PR Year</Label>
                        <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={newEmployee.pr_year} onChange={(e) => setNewEmployee({...newEmployee, pr_year: e.target.value})}>
                          <option value="">Select...</option>
                          {PR_YEARS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                      </div>
                    )}
                    {newEmployee.citizenship_status === 'foreigner' && (
                      <div>
                        <Label>Work Pass Type</Label>
                        <select className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" value={newEmployee.work_pass_type} onChange={(e) => setNewEmployee({...newEmployee, work_pass_type: e.target.value})}>
                          <option value="">Select...</option>
                          <option value="employment_pass">Employment Pass</option>
                          <option value="s_pass">S Pass</option>
                          <option value="work_permit">Work Permit</option>
                          <option value="dependant_pass">Dependant Pass</option>
                        </select>
                      </div>
                    )}
                    <div><Label>CPF Account No / NRIC</Label><Input value={newEmployee.cpf_account_number} onChange={(e) => setNewEmployee({...newEmployee, cpf_account_number: e.target.value})} placeholder="S1234567A" /></div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button type="submit" variant="teal">Add Employee</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Position</th>
                  <th className="text-left p-2">Manager</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Role</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => {
                  const citizenshipLabels = { singapore_citizen: 'SG Citizen', permanent_resident: 'PR', foreigner: 'Foreigner' };
                  return (
                    <tr key={emp.id} className="border-b">
                      <td className="p-2 font-medium">{emp.first_name} {emp.last_name}</td>
                      <td className="p-2 text-xs">{emp.email}</td>
                      <td className="p-2">{emp.position}</td>
                      <td className="p-2">
                        <button 
                          onClick={() => setShowManagerModal(emp)} 
                          className="text-left hover:underline text-blue-600 text-sm"
                          title="Click to change manager"
                        >
                          {getManagerName(emp.manager_id) || <span className="text-muted-foreground italic">Assign...</span>}
                        </button>
                      </td>
                      <td className="p-2"><Badge className={emp.is_active ? 'bg-green-500' : 'bg-red-500'}>{emp.is_active ? 'Active' : 'Inactive'}</Badge></td>
                      <td className="p-2">
                        {emp.is_admin && <Badge className="bg-purple-500 mr-1">Admin</Badge>}
                        <span className="text-xs text-muted-foreground capitalize">{emp.role?.replace('_', ' ') || 'employee'}</span>
                      </td>
                      <td className="p-2 space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(emp)}>{emp.is_active ? 'Deactivate' : 'Activate'}</Button>
                        {!emp.onboarding_complete && <Button variant="ghost" size="sm" onClick={() => assignOnboarding(emp.id)} title="Assign onboarding tasks"><UserCheck className="h-4 w-4" /></Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Manager Assignment Modal */}
      {showManagerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Assign Manager</CardTitle>
              <CardDescription>Select manager for {showManagerModal.first_name} {showManagerModal.last_name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <button
                  onClick={() => assignManager(showManagerModal.id, null)}
                  className="w-full p-3 text-left rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <span className="text-muted-foreground italic">No Manager (Remove)</span>
                </button>
                {managers.filter(m => m.id !== showManagerModal.id).map(manager => (
                  <button
                    key={manager.id}
                    onClick={() => assignManager(showManagerModal.id, manager.id)}
                    className={`w-full p-3 text-left rounded-lg border hover:bg-muted/50 transition-colors ${showManagerModal.manager_id === manager.id ? 'border-teal bg-teal/10' : ''}`}
                  >
                    <div className="font-medium">{manager.first_name} {manager.last_name}</div>
                    <div className="text-sm text-muted-foreground">{manager.position} • {manager.department}</div>
                  </button>
                ))}
              </div>
              <Button variant="outline" className="w-full" onClick={() => setShowManagerModal(null)}>Cancel</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const AdminPayrollConfigTab = ({ token }) => {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newConfig, setNewConfig] = useState({ name: '', description: '', config_type: 'deduction', calculation_type: 'percentage', value: 0, applies_to_countries: ['Singapore'] });

  const fetchConfigs = () => {
    fetch(`${API_URL}/api/hr/admin/payroll-config`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(r => r.json())
      .then(d => setConfigs(d.configs || []))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { fetchConfigs(); }, [token]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/hr/admin/payroll-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(newConfig),
      });
      if (!res.ok) throw new Error('Failed to create config');
      toast.success('Config created!');
      setShowAdd(false);
      fetchConfigs();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this config?')) return;
    try {
      await fetch(`${API_URL}/api/hr/admin/payroll-config/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      toast.success('Deleted');
      fetchConfigs();
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <h2 className="text-2xl font-bold">Payroll Configuration</h2>
        <Button variant="teal" onClick={() => setShowAdd(true)}><Plus className="h-4 w-4 mr-2" />Add Config</Button>
      </div>

      {showAdd && (
        <Card>
          <CardHeader><CardTitle>Add Payroll Configuration</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><Label>Name *</Label><Input value={newConfig.name} onChange={(e) => setNewConfig({...newConfig, name: e.target.value})} required placeholder="e.g., CPF (Employee)" /></div>
                <div><Label>Type</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3" value={newConfig.config_type} onChange={(e) => setNewConfig({...newConfig, config_type: e.target.value})}>
                    <option value="deduction">Deduction</option>
                    <option value="allowance">Allowance</option>
                    <option value="bonus">Bonus</option>
                    <option value="reimbursement">Reimbursement</option>
                  </select>
                </div>
                <div><Label>Calculation</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3" value={newConfig.calculation_type} onChange={(e) => setNewConfig({...newConfig, calculation_type: e.target.value})}>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div><Label>Value ({newConfig.calculation_type === 'percentage' ? '%' : 'SGD'})</Label><Input type="number" value={newConfig.value} onChange={(e) => setNewConfig({...newConfig, value: parseFloat(e.target.value) || 0})} /></div>
                <div className="col-span-2"><Label>Description</Label><Input value={newConfig.description || ''} onChange={(e) => setNewConfig({...newConfig, description: e.target.value})} /></div>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button type="submit" variant="teal">Add</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {configs.map(c => (
          <Card key={c.id}>
            <CardHeader className="pb-2">
              <div className="flex justify-between">
                <CardTitle className="text-lg">{c.name}</CardTitle>
                <Badge className={c.config_type === 'deduction' ? 'bg-red-500' : c.config_type === 'allowance' ? 'bg-green-500' : 'bg-blue-500'}>{c.config_type}</Badge>
              </div>
              {c.description && <CardDescription>{c.description}</CardDescription>}
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{c.calculation_type === 'percentage' ? `${c.value}%` : `$${c.value}`}</p>
              <p className="text-xs text-muted-foreground mt-2">Applies to: {c.applies_to_countries?.length > 3 ? 'All countries' : c.applies_to_countries?.join(', ')}</p>
              <Button variant="ghost" size="sm" className="mt-2 text-red-500" onClick={() => handleDelete(c.id)}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ==================== ADMIN PAYROLL RUN TAB ====================
const AdminPayrollRunTab = ({ token }) => {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);

  const fetchSummary = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/admin/payroll/summary?month=${selectedMonth}&year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, [selectedMonth, selectedYear, token]);

  const generatePayslips = async () => {
    setIsGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/admin/payroll/generate?month=${selectedMonth}&year=${selectedYear}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to generate payslips');
      const data = await res.json();
      toast.success(data.message);
      fetchSummary();
    } catch (err) { toast.error(err.message); }
    finally { setIsGenerating(false); }
  };

  const sendNotifications = async () => {
    setIsNotifying(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/admin/payroll/notify?month=${selectedMonth}&year=${selectedYear}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to send notifications');
      const data = await res.json();
      toast.success(`${data.sent} payslip notifications sent`);
      if (data.failed > 0) {
        toast.warning(`${data.failed} notifications failed to send`);
      }
    } catch (err) { toast.error(err.message); }
    finally { setIsNotifying(false); }
  };

  const downloadExport = async (type) => {
    try {
      const endpoint = type === 'bank' ? 'bank-payment' : type === 'cpf' ? 'cpf-submission' : 'sdl-submission';
      const res = await fetch(`${API_URL}/api/hr/admin/payroll/export/${endpoint}?month=${selectedMonth}&year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${endpoint}_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} export downloaded`);
    } catch (err) { toast.error(err.message); }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <h2 className="text-2xl font-bold">Payroll Run</h2>
        <div className="flex items-center gap-2">
          <select className="h-10 rounded-md border border-input bg-background px-3" value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
            {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="h-10 rounded-md border border-input bg-background px-3" value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><Users className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="text-2xl font-bold">{summary?.employee_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100"><DollarSign className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Gross Pay</p>
                <p className="text-2xl font-bold">${summary?.totals?.gross?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100"><Shield className="h-5 w-5 text-orange-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Total CPF</p>
                <p className="text-xl font-bold">${((summary?.totals?.cpf_employee || 0) + (summary?.totals?.cpf_employer || 0)).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">EE: ${summary?.totals?.cpf_employee?.toLocaleString() || 0} | ER: ${summary?.totals?.cpf_employer?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal/20"><CreditCard className="h-5 w-5 text-teal" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Net Pay</p>
                <p className="text-2xl font-bold text-teal">${summary?.totals?.net?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5" /> {monthNames[selectedMonth - 1]} {selectedYear} Payroll</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="teal" onClick={generatePayslips} disabled={isGenerating} data-testid="generate-payslips-btn">
              {isGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              Generate Payslips
            </Button>
            <Button variant="outline" onClick={sendNotifications} disabled={!summary?.employee_count || isNotifying} data-testid="send-notifications-btn" className="border-blue-500 text-blue-600 hover:bg-blue-50">
              {isNotifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
              Notify Employees
            </Button>
            <Separator orientation="vertical" className="h-10" />
            <Button variant="outline" onClick={() => downloadExport('bank')} disabled={!summary?.employee_count} data-testid="export-bank-btn">
              <Download className="h-4 w-4 mr-2" />Bank Payment CSV
            </Button>
            <Button variant="outline" onClick={() => downloadExport('cpf')} disabled={!summary?.employee_count} data-testid="export-cpf-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />CPF Submission
            </Button>
            <Button variant="outline" onClick={() => downloadExport('sdl')} disabled={!summary?.employee_count} data-testid="export-sdl-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />SDL Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payslip List */}
      {summary?.payslips?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Payslips for {monthNames[selectedMonth - 1]} {selectedYear}</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Employee</th>
                    <th className="text-right p-2">Base</th>
                    <th className="text-right p-2">CPF (EE)</th>
                    <th className="text-right p-2">CPF (ER)</th>
                    <th className="text-right p-2">Deductions</th>
                    <th className="text-right p-2">Net</th>
                    <th className="text-left p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.payslips.map(p => (
                    <tr key={p.id} className="border-b">
                      <td className="p-2">{p.employee_id.slice(0, 8)}...</td>
                      <td className="p-2 text-right">${p.base_salary?.toFixed(2)}</td>
                      <td className="p-2 text-right text-orange-600">${p.deductions?.['CPF (Employee)']?.toFixed(2) || '0.00'}</td>
                      <td className="p-2 text-right text-orange-600">${p.cpf_employer?.toFixed(2) || '0.00'}</td>
                      <td className="p-2 text-right text-red-600">-${p.total_deductions?.toFixed(2)}</td>
                      <td className="p-2 text-right font-semibold text-teal">${p.net_salary?.toFixed(2)}</td>
                      <td className="p-2"><Badge className={p.payment_status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'}>{p.payment_status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminAuditLogTab = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/hr/admin/audit-logs?limit=100`, { headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include' })
      .then(r => r.json())
      .then(d => setLogs(d.logs || []))
      .finally(() => setIsLoading(false));
  }, [token]);

  if (isLoading) return <div className="flex justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Audit Logs</h2>
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {logs.map(log => (
              <div key={log.id} className="flex items-start gap-4 p-3 rounded-lg border text-sm">
                <History className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <div className="flex justify-between">
                    <span className="font-medium">{log.action}</span>
                    <span className="text-muted-foreground">{new Date(log.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-muted-foreground">{log.employee_email}</p>
                  {log.details && <p className="text-xs text-muted-foreground mt-1">{log.details}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== MAIN HR PORTAL ====================
export const HRPortal = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [token, setToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [isAuthCallback, setIsAuthCallback] = useState(false);

  useEffect(() => {
    // Check for OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setIsAuthCallback(true);
      return;
    }

    // Check existing session
    const savedToken = localStorage.getItem('hr_token');
    const savedEmployee = localStorage.getItem('hr_employee');
    const savedIsAdmin = localStorage.getItem('hr_is_admin') === 'true';

    if (savedToken && savedEmployee) {
      // Verify session with backend
      fetch(`${API_URL}/api/hr/auth/me`, {
        headers: { 'Authorization': `Bearer ${savedToken}` },
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) throw new Error('Session expired');
          return res.json();
        })
        .then(data => {
          setToken(savedToken);
          setEmployee(data);
          setIsAdmin(data.is_admin || savedIsAdmin);
          setIsAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem('hr_token');
          localStorage.removeItem('hr_employee');
          localStorage.removeItem('hr_is_admin');
          setIsAuthenticated(false);
        });
    } else {
      setIsAuthenticated(false);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleAuthSuccess = (emp, tok, admin) => {
    setEmployee(emp);
    setToken(tok);
    setIsAdmin(admin);
    setIsAuthenticated(true);
    setIsAuthCallback(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/hr/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
    } catch (e) {}
    localStorage.removeItem('hr_token');
    localStorage.removeItem('hr_employee');
    localStorage.removeItem('hr_is_admin');
    setEmployee(null);
    setToken(null);
    setIsAdmin(false);
    setIsAuthenticated(false);
  };

  if (isAuthCallback) {
    return <AuthCallback onAuthSuccess={handleAuthSuccess} />;
  }

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <Loader2 className="h-12 w-12 animate-spin text-teal" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  const employeeTabs = [
    { key: 'profile', label: 'Profile', icon: User },
    { key: 'timesheet', label: 'Timesheet', icon: Clock },
    { key: 'leave', label: 'Leave', icon: Calendar },
    { key: 'team', label: 'Team', icon: Users },
    { key: 'payroll', label: 'Payroll', icon: DollarSign },
    { key: 'documents', label: 'Documents', icon: FileText },
    { key: 'onboarding', label: 'Tasks', icon: ClipboardList },
  ];

  const adminTabs = [
    { key: 'admin-approvals', label: 'Approvals', icon: CheckCircle },
    { key: 'admin-employees', label: 'Employees', icon: UserCheck },
    { key: 'admin-payroll-run', label: 'Payroll', icon: Play },
    { key: 'admin-payroll', label: 'Config', icon: Settings },
    { key: 'admin-audit', label: 'Audit', icon: History },
  ];

  const allTabs = isAdmin ? [...employeeTabs, ...adminTabs] : employeeTabs;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white border-b border-navy-foreground/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src="https://customer-assets.emergentagent.com/job_1f762a5e-4e34-4452-aabe-298bcfb2d13e/artifacts/9emip28a_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg" alt="SusTek" className="h-8 brightness-0 invert" />
              <Separator orientation="vertical" className="h-6 bg-white/20" />
              <span className="text-sm font-medium text-white/80">HR Portal</span>
              {isAdmin && <Badge className="bg-purple-500">Admin</Badge>}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium">{employee?.first_name} {employee?.last_name}</p>
                <p className="text-xs text-white/60">{employee?.position}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/80 hover:text-white hover:bg-white/10">
                <LogOut className="h-4 w-4 mr-2" />Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid w-full mb-8 ${isAdmin ? 'grid-cols-6 sm:grid-cols-12' : 'grid-cols-4 sm:grid-cols-7'}`}>
            {allTabs.map(tab => (
              <TabsTrigger key={tab.key} value={tab.key} className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm" data-testid={`tab-${tab.key}`}>
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="profile"><ProfileTab employee={employee} token={token} onProfileUpdate={setEmployee} /></TabsContent>
          <TabsContent value="timesheet"><TimesheetTab token={token} /></TabsContent>
          <TabsContent value="leave"><LeaveTab token={token} /></TabsContent>
          <TabsContent value="team"><TeamCalendarTab token={token} /></TabsContent>
          <TabsContent value="payroll"><PayrollTab token={token} employee={employee} /></TabsContent>
          <TabsContent value="documents"><DocumentsTab token={token} employee={employee} /></TabsContent>
          <TabsContent value="onboarding"><OnboardingTab token={token} employee={employee} /></TabsContent>
          {isAdmin && (
            <>
              <TabsContent value="admin-approvals"><ManagerApprovalsTab token={token} /></TabsContent>
              <TabsContent value="admin-employees"><AdminEmployeesTab token={token} /></TabsContent>
              <TabsContent value="admin-payroll-run"><AdminPayrollRunTab token={token} /></TabsContent>
              <TabsContent value="admin-payroll"><AdminPayrollConfigTab token={token} /></TabsContent>
              <TabsContent value="admin-audit"><AdminAuditLogTab token={token} /></TabsContent>
            </>
          )}
        </Tabs>
      </main>

      <footer className="border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-sm text-muted-foreground text-center">© {new Date().getFullYear()} SusTek Pte. Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default HRPortal;
